import { browserDownload } from "@app/lib/utils-browser";
import { queryClient } from "@app/QueryClientWithHeaders";
import {
    type GetSuppliers_DeprecatedQuery,
    type QuestionnaireFilterInput,
    type SupplierFilter,
    type SupplierSort,
    type SupplierTemplateQuestionFilterInput,
    type UserFilter,
} from "@generated/client/graphql";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GraphQLSupplierRepository } from "../../../repositories/GraphQLRepositories/SupplierRepository";

const supplierRepository = new GraphQLSupplierRepository();

export type Supplier = Exclude<
    GetSuppliers_DeprecatedQuery["suppliers_deprecated"],
    null | undefined
>[number];

export const supplierKeys = {
    all: ["suppliers"] as const,
    listAll: () => [...supplierKeys.all, "list"] as const,
    list: (
        filter: SupplierFilter = {},
        pagination: {
            skip?: number;
            take?: number;
        } = {},
        orderBy: SupplierSort = {},
    ) =>
        [
            ...supplierKeys.listAll(),
            JSON.stringify(filter),
            JSON.stringify(pagination),
            JSON.stringify(orderBy),
        ] as const,
    suppliersMinimal: (
        filter: SupplierFilter = {},
        pagination: {
            skip?: number;
            take?: number;
        } = {},
        orderBy: SupplierSort = {},
    ) =>
        [
            ...supplierKeys.listAll(),
            "minimal",
            JSON.stringify(filter),
            JSON.stringify(pagination),
            JSON.stringify(orderBy),
        ] as const,
    supplierQuestionnaireNames: () =>
        [...supplierKeys.all, "supplierQuestionnaireNames"] as const,
    oneAll: () => [...supplierKeys.all, "one"] as const,
    one: (id?: string | null) => [...supplierKeys.oneAll(), id] as const,
    usersAll: () => [...supplierKeys.all, "users"] as const,
    users: (filter?: UserFilter) =>
        [...supplierKeys.usersAll(), JSON.stringify(filter)] as const,
    questionsAll: () => [...supplierKeys.all, "questions"] as const,
    brand: (id?: string) => [...supplierKeys.one(id), "brand"] as const,
    templateQuestionnaire: () =>
        [...supplierKeys.questionsAll(), "template"] as const,
    templateQuestionsListAll: () =>
        [...supplierKeys.questionsAll(), "templateQuestionsList"] as const,
    templateQuestionsList: (
        filter: SupplierTemplateQuestionFilterInput = {},
        pagination: {
            skip?: number;
            take?: number;
        } = {},
    ) =>
        [
            ...supplierKeys.templateQuestionsListAll(),
            JSON.stringify(filter),
            JSON.stringify(pagination),
        ] as const,
    templateQuestionsListMinimal: (
        filter: SupplierTemplateQuestionFilterInput = {},
        pagination: {
            skip?: number;
            take?: number;
        } = {},
    ) =>
        [
            ...supplierKeys.templateQuestionsListAll(),
            "minimal",
            JSON.stringify(filter),
            JSON.stringify(pagination),
        ] as const,
    templateQuestionGroups: () =>
        [
            ...supplierKeys.templateQuestionsListAll(),
            "templateQuestionGroups",
        ] as const,
    supplierQuestionnairesListAll: () =>
        [...supplierKeys.oneAll(), "supplierQuestionnairesList"] as const,
    supplierQuestionnairesList: (
        supplierId?: string,
        filter: QuestionnaireFilterInput = {},
        pagination: {
            skip?: number;
            take?: number;
        } = {},
    ) =>
        [
            ...supplierKeys.supplierQuestionnairesListAll(),
            supplierId,
            JSON.stringify(filter),
            JSON.stringify(pagination),
        ] as const,
};

export const useSuppliers = () => {
    const query = useQuery({
        queryKey: supplierKeys.all,
        queryFn: () => supplierRepository.getSuppliers_deprecated(),
    });
    return {
        suppliers: query.data?.suppliers_deprecated,
        isLoading: query.isLoading,
        refetch: query.refetch,
    };
};

export function useSuppliersList(
    filter: SupplierFilter = {},
    pagination: {
        skip?: number;
        take?: number;
    } = {},
    orderBy: SupplierSort = {},
) {
    const { data, ...query } = useQuery({
        queryKey: supplierKeys.list(filter, pagination, orderBy),
        queryFn: () =>
            supplierRepository.getSuppliers(filter, pagination, orderBy),
    });
    return {
        suppliers: data?.suppliers ?? { list: [], count: 0 },
        ...query,
    };
}

export const useSuppliersMinimal = (
    filter: SupplierFilter = {},
    orderBy: SupplierSort = {},
) => {
    const { data, ...query } = useQuery({
        queryKey: supplierKeys.suppliersMinimal(filter, {}, orderBy),
        queryFn: () =>
            supplierRepository.getSuppliersMinimal(filter, {}, orderBy),
    });
    return {
        suppliers: data?.suppliers?.list ?? [],
        ...query,
    };
};

export const useDeleteSuppliers = () => {
    const { mutate: deleteSuppliers, ...mutation } = useMutation({
        mutationFn: supplierRepository.deleteSuppliers,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.listAll(),
            });
        },
    });
    return { deleteSuppliers, ...mutation };
};

export const useSupplierQuestionnaireNames = () => {
    const { data, ...query } = useQuery({
        queryKey: supplierKeys.supplierQuestionnaireNames(),
        queryFn: () => supplierRepository.getSupplierQuestionnaireNames(),
    });
    return {
        supplierQuestionnaireNames: data?.supplierQuestionnaireNames ?? [],
        ...query,
    };
};

export const useCloseQuestionnaires = () => {
    const { mutate: closeQuestionnaires, ...mutation } = useMutation({
        mutationFn: supplierRepository.closeSupplierQuestionnaires,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.all,
            });
        },
    });
    return { closeQuestionnaires, ...mutation };
};

export const useReOpenQuestionnaires = () => {
    const { mutate: reOpenQuestionnaires, ...mutation } = useMutation({
        mutationFn: supplierRepository.reOpenSupplierQuestionnaires,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.all,
            });
        },
    });
    return { reOpenQuestionnaires, ...mutation };
};

export const useOpenQuestionnaires = () => {
    const { mutate: openQuestionnaires, ...mutation } = useMutation({
        mutationFn: supplierRepository.openSupplierQuestionnaires,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.all,
            });
        },
    });
    return { openQuestionnaires, ...mutation };
};

export const useSendQuestionnaireEmail = () => {
    const { mutate: sendQuestionnaireEmail, ...mutation } = useMutation({
        mutationFn: supplierRepository.sendSupplierQuestionnaireEmail,
    });

    return { sendQuestionnaireEmail, ...mutation };
};

export const useSendQuestionnaireEmails = () => {
    const { mutate: sendQuestionnaireEmails, ...mutation } = useMutation({
        mutationFn: supplierRepository.sendSupplierQuestionnaireEmails,
    });

    return { sendQuestionnaireEmails, ...mutation };
};

export const useExportSuppliersList = () => {
    const { mutate: exportSuppliersList, ...mutation } = useMutation({
        mutationFn: supplierRepository.exportSuppliersList,
        onSettled: (data) => {
            if (data?.exportSuppliersList?.data?.signedUrl) {
                browserDownload(data.exportSuppliersList.data.signedUrl, true);
            }
        },
    });
    return { exportSuppliersList, ...mutation };
};

export const useExportLastQuestionnaireOfSuppliers = () => {
    const { mutate: exportLastQuestionnaireOfSuppliers, ...mutation } =
        useMutation({
            mutationFn: supplierRepository.exportLastQuestionnaireOfSuppliers,
            onSettled: (data) => {
                if (data?.exportLastQuestionnaireOfSuppliers?.data?.signedUrl) {
                    browserDownload(
                        data.exportLastQuestionnaireOfSuppliers.data.signedUrl,
                    );
                }
            },
        });

    return { exportLastQuestionnaireOfSuppliers, ...mutation };
};

export const useExportLastQuestionnaireFilesOfSuppliers = () => {
    const { mutate: exportLastQuestionnaireFilesOfSuppliers, ...mutation } =
        useMutation({
            mutationFn:
                supplierRepository.exportLastQuestionnaireFilesOfSuppliers,
            onSettled: (data) => {
                if (
                    data?.exportLastQuestionnaireFilesOfSuppliers?.data
                        ?.signedUrl
                ) {
                    browserDownload(
                        data.exportLastQuestionnaireFilesOfSuppliers.data
                            .signedUrl,
                    );
                }
            },
        });

    return {
        exportLastQuestionnaireFilesOfSuppliers,
        ...mutation,
    };
};

export const useDownloadSuppliersImportTemplate = () => {
    const { mutate: downloadSuppliersImportTemplate, ...mutation } =
        useMutation({
            mutationFn: supplierRepository.downloadSuppliersImportTemplate,
            onSettled: (data) => {
                if (data?.downloadSuppliersImportTemplate?.data?.signedUrl) {
                    browserDownload(
                        data.downloadSuppliersImportTemplate.data.signedUrl,
                    );
                }
            },
        });

    return {
        downloadSuppliersImportTemplate,
        ...mutation,
    };
};
